// Shared lists for the project editor. TASK_OPTIONS and SUB_TASK_STATUSES are the same as the Settings form
// (ManageProjectManagement.js), so both editors offer identical dropdowns. Keep the two in step.
export const TASK_OPTIONS = [
    {
        name: 'Material Procurement & Delivery',
        subTasks: [
            'Material Procurement',
            'Material Delivery to Site',
            'Consumables Procurement (Client Scope)',
        ],
    },
    {
        name: 'Project Execution',
        subTasks: [
            'OCEMS Installation',
            'Sampling Line Installation',
            'Energy Meter Installation',
            'Flowmeter Installation',
            'Level Sensor Installation',
            'Equipment Status Integration',
            'Cable Lying Activity',
            'AutoChlorination Installation',
            'Chillmax Installation',
            'Quality Meters Installation',
            'DO Meter Installation',
        ],
    },
    {
        name: 'Automation Activity',
        subTasks: [
            'STP/SBR Matic Installation',
            'Float Switch Installation',
            'Automatic Logic Update',
        ],
    },
    {
        name: 'Commissioning and Handover',
        subTasks: [
            'Testing & Commissioning',
            'Dashboard Training',
            'Project Handover',
        ],
    },
];

export const SUB_TASK_STATUSES = ['Pending', 'In Progress', 'On Hold', 'Completed', 'Cancelled'];

export const PROJECT_TYPES = ['WaterCare AMC', 'WaterRefurb', 'WaterSense RTM'];

export const STEP_STATUS_OPTIONS = [
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'now', label: 'In progress' },
    { value: 'on_hold', label: 'On hold' },
    { value: 'done', label: 'Done' },
];

// A new project starts with the standard timeline: admin steps around the four standard tasks
const PRE_STEPS = [['Client onboarding', true], ['Internal kick-off meeting', false], ['Project feasibility', false], ['Client kick-off meeting', true], ['Budgeting & planning', false]];
const POST_STEPS = [['Handover to internal team', false, ''], ['Invoicing', true, 'After handover'], ['Calibration schedule', true, 'Every 3 months']];

let keySeq = 0;
export const newKey = () => `k${Date.now().toString(36)}${(keySeq++).toString(36)}`;

export const emptySubTask = () => ({ key: newKey(), id: null, sub_task_name: '', start_date: '', end_date: '', description: '', spoc: '', status: 'Pending', attachments: [] });
export const emptyTask = (name = '') => ({ kind: 'task', key: newKey(), task_id: null, task_name: name, client_visible: true, sub_tasks: [] });
export const emptyStep = (label = '', clientVisible = true, dateLabel = '') => ({ kind: 'step', key: newKey(), label, client_visible: clientVisible, status: 'upcoming', date_label: dateLabel, start_date: '', end_date: '' });

export const standardItems = () => [
    ...PRE_STEPS.map(([l, c]) => emptyStep(l, c)),
    ...TASK_OPTIONS.map(t => emptyTask(t.name)),
    ...POST_STEPS.map(([l, c, d]) => emptyStep(l, c, d)),
];

// Name suggestions, merged exactly like the Settings form: built-in list + names learned from saved projects
export const taskNameOptions = (taskTags) => {
    const names = new Set(TASK_OPTIONS.map(t => t.name));
    taskTags.forEach(t => { if (t.task_name) names.add(t.task_name); });
    return Array.from(names);
};
export const subTaskNameOptions = (taskTags, taskName) => {
    const names = new Set(TASK_OPTIONS.find(t => t.name === taskName)?.subTasks || []);
    (taskTags.find(t => t.task_name === taskName)?.sub_task_names || []).forEach(n => names.add(n));
    return Array.from(names);
};

// SPOC is stored as a JSON string {id: email, name}, the same as the Settings form
export const userLabel = (u) => ((u?.name || u?.label) ? (u.user_id ? `${u.name || u.label} (${u.user_id})` : (u.name || u.label)) : '');
export const spocUser = (spoc, users) => {
    try { const p = JSON.parse(spoc || '{}'); return users.find(o => o.user_id === p.id) || null; } catch { return users.find(o => o.name === spoc) || null; }
};
export const spocName = (spoc) => {
    if (!spoc) return '';
    try { const p = JSON.parse(spoc); return p?.name || p?.id || ''; } catch { return spoc === '[object Object]' ? '' : spoc; }
};
export const toSpoc = (u) => (u ? JSON.stringify({ id: u.user_id, name: u.name }) : '');

// The same checks as the Settings form, so anything saved here can be saved there too.
// Returns { message, errors } where errors is keyed by item key / sub-task key.
export const validateDraft = (draft) => {
    const errors = {};
    const problems = [];
    if (!draft.project.facility_id) { errors.facility = 'Facility is required'; problems.push('Pick a facility'); }
    draft.items.forEach((it, i) => {
        if (it.kind === 'task') {
            if (!it.task_name.trim()) { errors[it.key] = 'Task Name is required'; problems.push(`Item ${i + 1}: Task Name is required`); }
            it.sub_tasks.forEach((st) => {
                const e = {};
                if (!st.sub_task_name.trim()) e.sub_task_name = 'Sub-task Name is required';
                if (!st.start_date) e.start_date = 'Start Date is required';
                if (!st.end_date) e.end_date = 'End Date is required';
                else if (st.start_date && st.end_date < st.start_date) e.end_date = 'End Date must be on or after Start Date';
                if (Object.keys(e).length) { errors[st.key] = e; problems.push(`“${it.task_name || 'Task'}”: ${Object.values(e)[0]}`); }
            });
        } else if (!it.label.trim()) { errors[it.key] = 'Step name is required'; problems.push(`Item ${i + 1}: step name is required`); }
    });
    return { errors, message: problems.length ? `${problems[0]}${problems.length > 1 ? ` (+${problems.length - 1} more)` : ''}` : null };
};
